// @ts-nocheck — ported from design-source/Pay-Assistant Desktop.dc.html; live data instead of the demo list.
//
// Desktop tray window. Flow per invoice: copy amount → user pastes in the bank
// (⌘V/Ctrl+V, detected system-wide by the Rust key watcher) → copy
// communication → paste → payment is booked. Booking waits for the 6 s undo
// window, so "Rückgängig" never has to reverse a server-side payment.
import { emit, listen } from '@tauri-apps/api/event';
import { DCLogic } from '../../dc/runtime';
import { tr2 } from '../../i18n/lang';
import { applyLang } from '../../i18n/apply';
import { api, errorText } from '../../api/client';
import { toCents, toDecimal } from '../../api/money';
import { markPaid } from '../../data/actions';
import { borrowToken, copyText, EV, paActive, paHide, paMove, paSetActive, paPin, paPosition, paResize, paStatus } from '../../desktop/bridge';

const UNDO_MS = 6000;
const savedScale = () => { try { const v = Number(localStorage.getItem('pa.scale')); return v >= 0.8 && v <= 1.4 ? v : 1; } catch { return 1; } };
const MOD = /Mac/i.test(navigator.platform) ? '⌘C' : 'Ctrl+C';

class Component extends DCLogic {
  state = { sc: savedScale(), on: false, open: true, pin: false, acct: 'BIL', i: 0, step: 0, paid: [], undo: null, pos: null, drag: null, inv: [], note: 'Wird geladen …' };
  F = [['amt', 'MONTANT', 'Betrag'], ['com', 'COMMUNICATION', 'Comm.']];

  get INV() { return this.state.inv; }

  async componentDidMount() {
    this.kd = e => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && (e.key === 'v' || e.key === 'V')) { e.preventDefault(); this.pasted(); }
      else if (mod && (e.key === 'c' || e.key === 'C')) { e.preventDefault(); this.confirm(); }
      else if (e.key === 'Escape' && !this.state.pin) paHide();
      // Size like everywhere on the Mac: ⌘+ / ⌘− / ⌘0.
      else if (mod && (e.key === '+' || e.key === '=')) { e.preventDefault(); this.scale(this.state.sc + 0.1); }
      else if (mod && e.key === '-') { e.preventDefault(); this.scale(this.state.sc - 0.1); }
      else if (mod && e.key === '0') { e.preventDefault(); this.scale(1); }
    };
    window.addEventListener('keydown', this.kd);
    this.offs = await Promise.all([
      listen(EV.pasted, () => this.pasted()),
      listen(EV.confirm, () => this.confirm()),
      listen(EV.reload, () => this.load()),
      listen(EV.paid, () => this.load()),
      // The one on/off state (app switch, tray icon, ⌘⌥P): on again starts with the first value.
      listen(EV.active, (e) => { const on = !!e.payload; if (on !== this.state.on) this.setState({ on, step: 0 }, () => on && this.copy(0)); }),
      // Main window's session: new rights → reload the queue; signed out (also by RimoID) → nothing left to show.
      listen(EV.session, (e) => { if (e.payload === 'lost') { this.setState({ inv: [], note: 'Abgemeldet · bitte in RimoCompta wieder anmelden.' }); paHide(); } else this.load(true); }),
    ]);
    this.ro = new ResizeObserver(() => this.fit());
    const pop = document.querySelector('[data-pa-pop]');
    if (pop) this.ro.observe(pop);
    this.setState({ on: await paActive() });
    this.poll = setInterval(() => this.load(true), 60_000);
    await this.load();
  }
  componentWillUnmount() {
    window.removeEventListener('keydown', this.kd); clearTimeout(this.ut); clearTimeout(this.at); clearInterval(this.poll);
    (this.offs || []).forEach(un => un()); this.ro && this.ro.disconnect();
    this.commit();
  }
  /** Current step next to the tray icon, like the design's menu bar item. */
  status() { const S = this.state, inv = this.INV[S.i], f = S.step < this.F.length ? this.F[S.step] : null, t = S.on && inv ? (f ? f[2] : 'Bestätigen ' + MOD) : ''; if (t !== this._st) { this._st = t; paStatus(t); } }
  componentDidUpdate() { this.status(); const pop = document.querySelector('[data-pa-pop]'); if (pop && this.ro && pop !== this._obs) { this._obs = pop; this.ro.observe(pop); } this.fit(); }
  /** The window always wraps the popover (+ room for its shadow). */
  fit() {
    const pop = document.querySelector('[data-pa-pop]'); if (!pop) return;
    // transform:scale doesn't change layout: unscaled size × scale, plus the 8 px offset and room for the shadow.
    const sc = this.state.sc, h = Math.ceil(8 + pop.offsetHeight * sc + 12), w = Math.ceil(8 + pop.offsetWidth * sc + 8);
    if (h !== this._h || w !== this._w) { this._h = h; this._w = w; paResize(h, w); }
  }
  /** Scale 80–140 % (grip, ⌘+ ⌘− ⌘0), remembered for the next start. */
  scale(v) { const sc = Math.round(Math.min(1.4, Math.max(0.8, v)) * 100) / 100; if (sc === this.state.sc) return; try { localStorage.setItem('pa.scale', String(sc)); } catch {} this.setState({ sc }, () => this.fit()); }

  async load(quiet) {
    const tok = await borrowToken();
    if (!tok) return this.setState({ inv: [], note: tr2('Bitte zuerst in RimoCompta anmelden.', 'Veuillez d’abord vous connecter à RimoCompta.') });
    // The language of the person (RimoID claim `locale` in the borrowed token; user decision 15.10.2026).
    try { applyLang(JSON.parse(atob(tok.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).locale); } catch { /* keep the current language */ }
    try {
      const [queue, accounts] = await Promise.all([
        api('/compta/payments/queue'),
        api('/compta/payments/bank-accounts').catch(() => []),
      ]);
      const pending = this.pending ? this.pending.inv.id : null;
      const inv = queue.filter(q => q.id !== pending).map(q => ({
        id: q.id, version: q.version, sup: q.issuerName || '—', no: q.documentNumber || '—',
        amt: toDecimal(toCents(q.suggestedAmount)), esc: q.discount && q.discount.active ? q.discount.rate : null,
        comm: q.communication || 'Facture ' + (q.documentNumber || ''),
      }));
      const S = this.state, cur = S.inv[S.i];
      const i = cur ? Math.max(0, inv.findIndex(x => x.id === cur.id)) : 0;
      const def = (accounts.find(a => a.defaultForPayment) || {}).bank;
      this.setState({ inv, i, paid: [], note: inv.length ? '' : 'Keine Rechnungen zu bezahlen.', ...(def && !this._acctSet ? { acct: def } : {}) }, () => {
        if (!quiet || !cur || !inv[i] || inv[i].id !== cur.id) this.copy(quiet && cur && inv[i] && inv[i].id === cur.id ? S.step : 0);
      });
    } catch (e) { this.setState({ inv: [], note: errorText(e) }); }
  }

  fmt(dec, acct) { return acct === 'BIL' ? dec.replace('.', ',') : dec; }
  val(k, inv, acct) { return k === 'amt' ? this.fmt(inv.amt, acct) : inv.comm; }
  copy(step) {
    const S = this.state, inv = this.INV[S.i]; if (!inv || !S.on) return;
    copyText(this.val(this.F[step][0], inv, S.acct));
    this.setState({ step, flash: Date.now() });
  }
  pasted() {
    const S = this.state; if (!S.on || !this.INV[S.i] || S.step >= this.F.length) return;
    if (S.step < this.F.length - 1) return this.copy(S.step + 1);
    // Everything is pasted: the payment is booked only after an explicit ⌘C / Ctrl+C.
    this.setState({ step: this.F.length });
  }
  confirm() { const S = this.state; if (S.on && this.INV[S.i] && S.step >= this.F.length) this.next(true); }

  /** Books the payment that is waiting out its undo window. */
  commit() {
    const p = this.pending; if (!p) return; this.pending = null; clearTimeout(this.ut);
    markPaid(p.inv, { amount: p.inv.amt, bank: p.acct, communication: p.inv.comm })
      .then(() => emit(EV.paid, { documentId: p.inv.id }))
      .catch(e => { this.setState({ note: p.inv.no + ': ' + errorText(e) }); this.load(true); });
  }

  next(paid) {
    clearTimeout(this.at);
    const S = this.state, prev = S.i, P = paid ? [...S.paid, prev] : S.paid;
    if (paid) { this.commit(); this.pending = { inv: this.INV[prev], acct: S.acct }; this.ut = setTimeout(() => { this.commit(); this.setState({ undo: null }); }, UNDO_MS); }
    const rest = this.INV.map((_, k) => k).filter(k => !P.includes(k));
    let n = rest.find(k => k > prev); if (n === undefined) n = rest.find(k => k !== prev);
    if (n === undefined) n = rest.length ? rest[0] : -1;
    this.setState({ paid: P, i: n, step: 0, undo: paid ? { i: prev, acct: S.acct } : null }, () => n >= 0 && this.copy(0));
  }

  renderVals() {
    const S = this.state, inv = this.INV[S.i], acct = S.acct, AC = { BIL: '#702F8B', BGL: '#10AA63' };
    const rest = this.INV.map((_, k) => k).filter(k => !S.paid.includes(k));
    const f = S.step < this.F.length ? this.F[S.step] : null;
    const cv = inv && f ? this.val(f[0], inv, acct) : '';
    return {
      sc: 1, open: true, isOff: !S.on,
      counter: inv && S.on ? (rest.indexOf(S.i) + 1) + ' / ' + rest.length : '',
      mb: { bg: 'transparent', op: 1, whiteD: 'none', colorD: 'none', dotD: 'none', txtD: 'none', txt: '' },
      pop: { top: '8px', left: '8px', cur: 'grab', pinBg: S.pin ? 'rgba(19,117,189,.3)' : 'transparent', pinFg: S.pin ? '#8CC0EA' : '#9B9698', pinT: S.pin ? 'Lösen' : 'Anheften' },
      sw: S.on ? { bg: '#05A79D', x: '16px', t: 'Ausschalten' } : { bg: 'rgba(255,255,255,.2)', x: '2px', t: 'Einschalten' },
      accts: ['BIL', 'BGL'].map(k => ({ t: k, dBil: k === 'BIL' ? 'block' : 'none', dBgl: k === 'BGL' ? 'block' : 'none', bg: acct === k ? AC[k] : 'transparent', op: acct === k ? 1 : .5,
        pick: e => { e.stopPropagation(); this._acctSet = true; this.setState({ acct: k }, () => S.step < this.F.length && this.copy(S.step)); } })),
      hasInv: S.on && !!inv, allDone: S.on && !inv,
      doneText: S.paid.length ? 'Alle ' + S.paid.length + ' Rechnungen bezahlt' : S.note || 'Keine Rechnungen zu bezahlen.',
      inv: inv ? { sup: inv.sup, no: inv.no, amt: this.fmt(inv.amt, acct) + ' €', tag: inv.esc ? 'Escompte ' + inv.esc + ' % abgezogen' : 'Ohne Escompte', tagFg: inv.esc ? '#6FD3CB' : '#8C8789' } : {},
      cur: f ? { label: f[1] + ' · KOPIERT', val: cv, font: "600 14px/1.2 'JetBrains Mono',monospace", bg: 'rgba(19,117,189,.16)', bd: 'rgba(19,117,189,.55)', lFg: '#8CC0EA', icoD: 'block', chkD: 'none' }
        : { label: 'ALLES EINGEFÜGT', val: tr2('Mit ' + MOD + ' als bezahlt bestätigen', 'Confirmer comme payé avec ' + MOD), font: '500 13px/1.2 Inter,sans-serif', bg: 'rgba(5,167,157,.16)', bd: 'rgba(5,167,157,.55)', lFg: '#6FD3CB', icoD: 'none', chkD: 'block' },
      recopy: () => S.step < this.F.length && this.copy(S.step),
      steps: this.F.map(([, label, short], n) => ({ label: short, t: label, bg: n < S.step ? '#05A79D' : n === S.step ? '#1375BD' : 'rgba(255,255,255,.14)', fg: n === S.step ? '#FBFAFA' : n < S.step ? '#6FD3CB' : '#8C8789', pick: () => this.copy(n) })),
      showUndo: !!S.undo, undoText: S.undo && this.INV[S.undo.i] ? this.INV[S.undo.i].sup + ' · payée par ' + S.undo.acct : '',
      undo: () => { clearTimeout(this.ut); this.pending = null; const u = S.undo; this.setState({ paid: S.paid.filter(k => k !== u.i), i: u.i, step: 0, undo: null }, () => this.copy(0)); },
      markPaid: () => this.next(true), skip: () => this.next(false),
      reset: () => { this.commit(); this.setState({ undo: null }); this.load(); },
      toggleOpen: e => { e.stopPropagation(); paHide(); },
      closeOutside: () => {},
      togglePin: e => { e.stopPropagation(); const pin = !S.pin; this.setState({ pin }); paPin(pin); },
      toggleOn: e => { e.stopPropagation(); paSetActive(!S.on); }, // the whole assistant: app switch and tray follow
      paZoom: S.sc,
      // The header always moves the window; moving it pins it (stays open, keeps its place). „Lösen“ docks it again.
      onDown: e => {
        if (e.button !== 0 || (e.target instanceof Element && e.target !== e.currentTarget && getComputedStyle(e.target).cursor === 'pointer')) return;
        e.preventDefault();
        // Moved by hand (pointer delta → window position): the native window drag needs the original mouse-down
        // event, which is gone once the IPC call arrives, so it often did nothing on macOS.
        const x0 = e.screenX, y0 = e.screenY;
        let raf = 0, last = null, moved = false, base = null, lastEv = null;
        paPosition().then(p => { base = p; if (lastEv) move(lastEv); });
        const move = ev => {
          lastEv = ev; if (!base) return;
          last = [base[0] + ev.screenX - x0, base[1] + ev.screenY - y0];
          if (!moved && Math.abs(ev.screenX - x0) + Math.abs(ev.screenY - y0) < 3) return;
          if (!moved) { moved = true; if (!this.state.pin) { this.setState({ pin: true }); paPin(true); } }
          if (!raf) raf = requestAnimationFrame(() => { raf = 0; if (last) paMove(last[0], last[1]); });
        };
        const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
        window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
      },
      gripDown: e => {
        e.preventDefault(); e.stopPropagation();
        const x0 = e.screenX, sc0 = S.sc;
        const move = ev => this.scale(sc0 + (ev.screenX - x0) / 272);
        const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
        window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
      },
      gripReset: e => { e.stopPropagation(); this.scale(1); },
      onMove: () => {}, onUp: () => {}
    };
  }
}

export default Component;
